import events from './event.js';
import Storage from './storage.js';
import Logger from './logger.js';
import products from './products.js';

/**
 * FrameZoomView
 *
 * A self-contained, gallery-style fullscreen overlay that shows the framed
 * artwork (the "tavla") enlarged and lets the user pan/pinch/zoom it.
 *
 * Two entry points feed it (see spec):
 *   1) Edit-mode ("förstora" in the customizer): pass the live `.mscp-stack`
 *      DOM node — it is cloned 1:1 so what you see is exactly what the
 *      customizer renders (correct frame layers + full-res photo + crop).
 *   2) Product card ("förstora"-knapp bredvid pennan): there is no live stack
 *      on the product page (only a flattened thumbnail), so we rebuild a stack
 *      from the session (cropBox + attributes) and load the FULL-RES photo, so
 *      the zoom stays sharp instead of upscaling a small thumbnail.
 *
 * Quality / iOS notes:
 *   - We render real DOM layers. The frame/passepartout/wall layers are painted
 *     by mindstore-frames' CSS (msfr.css) keyed off `.mscp-crop-wrapper
 *     .mscp-crop-preview .mscp-stack[data-*]`, so we MUST nest the stack under
 *     those two ancestor classes for the backgrounds to resolve.
 *   - No <canvas>, no toBlob, no getImageData — this avoids the iOS/WebKit
 *     canvas-memory and blob-URL issues this codebase has hit. The photo is a
 *     plain <img> the browser decodes and manages. Sharpness is bounded only by
 *     the source resolution (full-res photo + 2000px frame webp), never by a
 *     capped canvas.
 */
class FrameZoomView {
    constructor() {
        this.overlay = null;
        this.viewport = null;   // klippande behållare (overflow hidden)
        this.layer = null;      // det som transformeras (translate + scale)
        this.host = null;       // .mscp-crop-preview som håller stacken
        this.baseW = 0;         // bas-storlek (px) på host, före zoom-transform
        this.baseH = 0;

        // Zoom-state
        this.scale = 1;
        this.minScale = 1;
        this.maxScale = 4;
        this.pos = { x: 0, y: 0 }; // translate i px, applied EFTER skalning

        // Pekar-state (Pointer Events – samma kod för mus och touch)
        this.pointers = new Map();
        this.pinchStartDist = 0;
        this.pinchStartScale = 1;
        this.lastPan = null;

        this._onKeydown = this._onKeydown.bind(this);
    }

    /* ------------------------------------------------------------------ */
    /* DOM                                                                 */
    /* ------------------------------------------------------------------ */

    ensureDom() {
        if (this.overlay) return;

        this.overlay = document.createElement('div');
        this.overlay.className = 'mscp-frame-zoom-overlay';

        // Stängknapp
        const close = document.createElement('span');
        close.className = 'mscp-frame-zoom-close mscp-floating-button';
        close.innerHTML = (window.mindstore_object?.icon?.times) || '×';
        close.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.close();
        });
        this.overlay.appendChild(close);

        // Klippande viewport
        this.viewport = document.createElement('div');
        this.viewport.className = 'mscp-frame-zoom-viewport';
        this.overlay.appendChild(this.viewport);

        // Transform-lager
        this.layer = document.createElement('div');
        this.layer.className = 'mscp-frame-zoom-layer';
        this.viewport.appendChild(this.layer);

        // Ancestor-kedjan som msfr.css kräver för att måla ram-lagren:
        // .mscp-crop-wrapper .mscp-crop-preview .mscp-stack[...]
        const wrapper = document.createElement('div');
        wrapper.className = 'mscp-crop-wrapper';
        this.host = document.createElement('div');
        this.host.className = 'mscp-crop-preview';
        wrapper.appendChild(this.host);
        this.layer.appendChild(wrapper);

        // Enkla zoom-knappar (komplement till pinch/wheel)
        const controls = document.createElement('div');
        controls.className = 'mscp-frame-zoom-controls';
        const zoomOut = document.createElement('span');
        zoomOut.className = 'mscp-frame-zoom-btn';
        zoomOut.textContent = '−';
        zoomOut.addEventListener('click', () => this._zoomBy(1 / 1.4, this._center()));
        const zoomIn = document.createElement('span');
        zoomIn.className = 'mscp-frame-zoom-btn';
        zoomIn.textContent = '+';
        zoomIn.addEventListener('click', () => this._zoomBy(1.4, this._center()));
        controls.appendChild(zoomOut);
        controls.appendChild(zoomIn);
        this.overlay.appendChild(controls);

        // Klick på bakgrunden (utanför lagret) stänger
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay || e.target === this.viewport) this.close();
        });

        this._attachGestures();
        document.body.appendChild(this.overlay);
    }

    /* ------------------------------------------------------------------ */
    /* Öppna / stäng                                                       */
    /* ------------------------------------------------------------------ */

    /**
     * @param {object} opts
     * @param {'clone'|'session'} opts.source
     * @param {HTMLElement} [opts.stack]     Live .mscp-stack (source='clone')
     * @param {string}      [opts.photoUrl]  Full-res foto-url (source='session')
     */
    async open(opts = {}) {
        try {
            this.ensureDom();
            this.host.innerHTML = '';

            let stack = null;
            if (opts.source === 'clone' && opts.stack) {
                stack = this._prepareClone(opts.stack);
            } else {
                stack = await this._buildFromSession(opts.photoUrl);
            }
            if (!stack) {
                Logger.error('FrameZoomView: could not produce a stack to show.');
                return;
            }
            this.host.appendChild(stack);

            // Lås body-scroll
            document.body.classList.add('mscp-frame-zoom-open');
            document.addEventListener('keydown', this._onKeydown);

            this.overlay.classList.add('active');

            // Vänta på layout + att fotot fått sina mått innan vi passar in
            await this._waitForPhoto(stack);
            requestAnimationFrame(() => requestAnimationFrame(() => this._fit()));

            window.Mindstore?.DataLayer?.clickEvent?.('mscp_frame_zoom_open', opts.source || '');
        } catch (e) {
            Logger.error('FrameZoomView.open failed', e);
        }
    }

    close() {
        if (!this.overlay) return;
        this.overlay.classList.remove('active');
        document.body.classList.remove('mscp-frame-zoom-open');
        document.removeEventListener('keydown', this._onKeydown);
        this.pointers.clear();
        // Rensa host efter transition så minnet frigörs
        setTimeout(() => {
            if (this.host && !this.overlay.classList.contains('active')) {
                this.host.innerHTML = '';
            }
        }, 250);
    }

    _onKeydown(e) {
        if (e.key === 'Escape') this.close();
    }

    /* ------------------------------------------------------------------ */
    /* Stack-källor                                                        */
    /* ------------------------------------------------------------------ */

    /**
     * Entry (1): klona den levande stacken 1:1. Bas-storleken sätts till
     * källans faktiska px-storlek så att fotots inline-transform (px) förblir
     * giltig — förstoring sker sedan via zoom-lagrets transform.
     */
    _prepareClone(sourceStack) {
        const rect = sourceStack.getBoundingClientRect();
        this.baseW = Math.max(1, Math.round(rect.width));
        this.baseH = Math.max(1, Math.round(rect.height));

        const clone = sourceStack.cloneNode(true);

        // Ta bort interaktiva/överflödiga element som inte hör hemma i vyn
        clone.querySelectorAll(
            '.mscp-animation-overlay, .preview-container, .mscp-dropzone, ' +
            '.mscp-info-messages, #thumb_preview'
        ).forEach(el => el.remove());

        this.host.style.width = `${this.baseW}px`;
        this.host.style.height = `${this.baseH}px`;
        this.host.style.aspectRatio = 'auto';
        return clone;
    }

    /**
     * Entry (2): bygg en stack från sessionen och ladda full-res-fotot, så att
     * inzoomningen blir skarp (inte en uppskalad miniatyr).
     */
    async _buildFromSession(photoUrl) {
        const cropBox = Storage.get('cropBox');
        const size = Storage.get('size') || window.mscp?.defaults?.size || '40x50';
        const orientation = Storage.get('orientation') || window.mscp?.defaults?.orientation || 'portrait';
        let presentation = Storage.get('presentation') || 'empty';
        const paper = Storage.get('paper');
        const retouch = Storage.get('retouch');

        // Bas-storlek: en stor kvadrat som ryms i skärmen, med gott om
        // upplösningsutrymme. Fit-skalning krymper den sedan till skärmen.
        const base = Math.round(Math.min(window.innerWidth, window.innerHeight) * 0.9);
        this.baseW = base;
        this.baseH = base;
        this.host.style.width = `${base}px`;
        this.host.style.height = `${base}px`;
        this.host.style.aspectRatio = 'auto';

        // Bygg lagerstrukturen (samma som CropImageViewport.loadWrappers)
        const stack = document.createElement('div');
        stack.className = 'mscp-stack';
        stack.dataset.orientation = orientation;
        stack.dataset.presentation = presentation;
        stack.dataset.size = size;
        if (paper) stack.dataset.paper = paper;
        if (retouch) stack.dataset.retouch = retouch;

        // Default-ram för paper/presentation (samma källa som customizern)
        try {
            const frame = await products.getDefaultFrame(paper, presentation);
            if (frame) stack.dataset.frame = frame;
        } catch (e) { /* utan ram visas ändå foto + ev. pp */ }

        stack.appendChild(this._layer('wall'));
        stack.appendChild(this._layer('shadow'));

        const photoWrapper = document.createElement('div');
        photoWrapper.className = 'mscp-photo-wrapper';
        const cropContainer = document.createElement('div');
        cropContainer.className = 'crop-container';
        photoWrapper.appendChild(cropContainer);
        stack.appendChild(photoWrapper);

        stack.appendChild(this._layer('pp'));
        stack.appendChild(this._layer('ram'));
        stack.appendChild(this._layer('giftwrap'));

        const img = document.createElement('img');
        img.className = 'mscp-layer photo';
        img.decoding = 'async';
        img.src = photoUrl || Storage.get('thumbnailUrl') || '';
        cropContainer.appendChild(img);

        // Sätt crop-container aspect-ratio + transform när fotot laddats.
        // (Vi gör det efter att stacken monterats — se _applySessionCrop.)
        this._pendingSessionCrop = { cropContainer, img, cropBox, presentation };
        return stack;
    }

    _layer(name) {
        const el = document.createElement('div');
        el.className = `mscp-layer ${name}`;
        return el;
    }

    /**
     * Applicerar crop på den nybyggda sessions-stacken. Matchar
     * CropImageViewport.getTransformFromBox: skala foton så att cropBox-bredden
     * fyller crop-containern och förskjut till cropBox-origo.
     */
    _applySessionCrop() {
        const pend = this._pendingSessionCrop;
        if (!pend) return;
        const { cropContainer, img, cropBox, presentation } = pend;

        // 'empty' presentation = fritt foto (CSS sköter contain), ingen crop.
        if (presentation === 'empty' || !cropBox ||
            typeof cropBox.width !== 'number' || cropBox.width <= 0) {
            this._pendingSessionCrop = null;
            return;
        }

        const ar = (typeof cropBox.aspectRatio === 'number' && cropBox.aspectRatio > 0)
            ? cropBox.aspectRatio
            : (cropBox.width / cropBox.height);
        cropContainer.style.aspectRatio = String(ar);

        const natW = img.naturalWidth;
        const natH = img.naturalHeight;
        if (!natW || !natH) { this._pendingSessionCrop = null; return; }

        const containerW = cropContainer.getBoundingClientRect().width;
        const scale = containerW / (cropBox.width * natW);
        const offsetX = -cropBox.left * scale * natW;
        const offsetY = -cropBox.top * scale * natH;
        img.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;
        this._pendingSessionCrop = null;
    }

    async _waitForPhoto(stack) {
        const img = stack.querySelector('img.mscp-layer.photo');
        if (!img) return;
        if (!(img.complete && img.naturalWidth)) {
            await new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
                setTimeout(resolve, 4000); // skyddstimeout så vi aldrig hänger
            });
        }
        // decode() garanterar att pixlarna är klara att målas — undviker en
        // enstaka bildruta där CSS-lagren (ram/pp) hunnit ritas men fotot inte.
        try { await img.decode?.(); } catch (e) { /* redan dekodad / avbruten */ }
    }

    /* ------------------------------------------------------------------ */
    /* Zoom / pan                                                          */
    /* ------------------------------------------------------------------ */

    _center() {
        const r = this.viewport.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }

    /** Passa in innehållet i viewporten (min-skala = fit) och centrera. */
    _fit() {
        // Applicera ev. sessions-crop nu när layout finns
        this._applySessionCrop();

        const vp = this.viewport.getBoundingClientRect();
        if (!this.baseW || !this.baseH) return;
        const fit = Math.min(vp.width / this.baseW, vp.height / this.baseH);
        this.minScale = fit;
        this.maxScale = fit * 4;
        this.scale = fit;
        this.pos = { x: 0, y: 0 };
        this._apply();
    }

    _apply() {
        // transform-origin: center (CSS). Skala först, translate sen.
        this.layer.style.transform =
            `translate(-50%, -50%) translate(${this.pos.x}px, ${this.pos.y}px) scale(${this.scale})`;
    }

    _clamp() {
        const vp = this.viewport.getBoundingClientRect();
        const w = this.baseW * this.scale;
        const h = this.baseH * this.scale;
        const maxX = Math.max(0, (w - vp.width) / 2);
        const maxY = Math.max(0, (h - vp.height) / 2);
        this.pos.x = Math.min(maxX, Math.max(-maxX, this.pos.x));
        this.pos.y = Math.min(maxY, Math.max(-maxY, this.pos.y));
    }

    /** Zooma med faktorn `factor` runt skärmpunkten `focal` (clientX/Y). */
    _zoomBy(factor, focal) {
        const s0 = this.scale;
        let s1 = Math.min(this.maxScale, Math.max(this.minScale, s0 * factor));
        if (s1 === s0) return;

        const c = this._center();
        const f = { x: focal.x - c.x, y: focal.y - c.y };
        // Håll fokuspunkten stilla: t1 = f - (s1/s0)*(f - t0)
        this.pos.x = f.x - (s1 / s0) * (f.x - this.pos.x);
        this.pos.y = f.y - (s1 / s0) * (f.y - this.pos.y);
        this.scale = s1;
        this._clamp();
        this._apply();
    }

    _attachGestures() {
        const vp = this.viewport;

        vp.addEventListener('wheel', (e) => {
            e.preventDefault();
            const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
            this._zoomBy(factor, { x: e.clientX, y: e.clientY });
        }, { passive: false });

        vp.addEventListener('pointerdown', (e) => {
            vp.setPointerCapture?.(e.pointerId);
            this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            if (this.pointers.size === 1) {
                this.lastPan = { x: e.clientX, y: e.clientY };
            } else if (this.pointers.size === 2) {
                const pts = [...this.pointers.values()];
                this.pinchStartDist = this._dist(pts[0], pts[1]);
                this.pinchStartScale = this.scale;
                this.lastPan = null;
            }
        });

        vp.addEventListener('pointermove', (e) => {
            if (!this.pointers.has(e.pointerId)) return;
            this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
            const pts = [...this.pointers.values()];

            if (this.pointers.size >= 2) {
                // Pinch: zooma runt mittpunkten mellan fingrarna
                const dist = this._dist(pts[0], pts[1]);
                if (this.pinchStartDist > 0) {
                    const target = this.pinchStartScale * (dist / this.pinchStartDist);
                    const focal = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
                    this._zoomBy(target / this.scale, focal);
                }
            } else if (this.lastPan && this.scale > this.minScale + 1e-4) {
                // Pan (endast meningsfullt när vi är inzoomade)
                this.pos.x += e.clientX - this.lastPan.x;
                this.pos.y += e.clientY - this.lastPan.y;
                this.lastPan = { x: e.clientX, y: e.clientY };
                this._clamp();
                this._apply();
            }
        });

        const endPointer = (e) => {
            this.pointers.delete(e.pointerId);
            if (this.pointers.size === 1) {
                const p = [...this.pointers.values()][0];
                this.lastPan = { x: p.x, y: p.y };
            } else if (this.pointers.size === 0) {
                this.lastPan = null;
            }
        };
        vp.addEventListener('pointerup', endPointer);
        vp.addEventListener('pointercancel', endPointer);

        // Dubbelklick / dubbeltryck: växla mellan fit och 2.5x
        vp.addEventListener('dblclick', (e) => {
            e.preventDefault();
            if (this.scale > this.minScale + 1e-3) {
                this._fit();
            } else {
                this._zoomBy(2.5, { x: e.clientX, y: e.clientY });
            }
        });
    }

    _dist(a, b) {
        const dx = a.x - b.x, dy = a.y - b.y;
        return Math.sqrt(dx * dx + dy * dy);
    }
}

const frameZoomView = new FrameZoomView();

// Event-API så andra moduler kan öppna vyn utan direkt referens.
//   events.trigger('openFrameZoom', { source, stack|photoUrl })
events.add('openFrameZoom', (opts) => frameZoomView.open(opts || {}));

export default frameZoomView;
